import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import type { DealItemsActionType } from '@/lib/deal-events.types';
import { toast } from '@/hooks/use-toast';
import { showImportReportFromProcessedDatasource } from '@/lib/datasource-import-resolution';
import {
  pendingCreatedItemToastIds,
  replaceDealItemsInProgressWithSuccess,
} from '@/lib/deal-items-toast';
import { isDealEventsSseEnabled } from '@/lib/deal-sse-flag';
import {
  type DealItemWithVersions,
  DealItemsUpdatedV3Document,
} from '@/lib/graphql/generated/graphql';
import { useUndoDealItems } from '@/lib/hooks/useUndoDealItems';
import {
  ensureDealItemsStream,
  refreshDealItemsByIds,
  removeDealItems,
  stopDealItemsStream,
} from '@/lib/streaming/deal-items-stream-controller';
import {
  type DealItemFromQuery,
  mergeStreamedItem,
  normalizeSingle,
} from '@/lib/streaming/normalize-deal-items';
import { diffHighlightableFields } from '@/lib/utils/deal-items-highlight-diff';
import {
  markBackendConfirmed,
  resetValidationWorker,
} from '@/lib/validation/validation-service';
import { dealItemDescriptionsByActiveVersionIdState } from '@/states/deal-item-descriptions.state';
import { pendingOptimisticUpdatesState } from '@/states/pending-optimistic-updates.state';
import {
  dealItemsRefetchState,
  dealItemsSignalState,
  productsMapState,
} from '@/states/products.state';
import {
  ALL_HIGHLIGHTABLE_FIELDS,
  type ProductModification,
  recentlyModifiedProductsState,
  updateRecentlyModifiedProducts,
} from '@/states/recently-modified-products.state';
import { datasourceProcessedSignalAtom } from '@/states/shopify-category-fallback.state';
import { useSubscription } from '@apollo/client/react';
import { useAuth0 } from '@auth0/auth0-react';
import type { TFunction } from 'i18next';
import { useSetAtom, useStore } from 'jotai';

/** Carte dealItemId → historyId actif pour valider la pile undo contre le cache. */
function historyIdMapFromDealItems(
  items: DealItemFromQuery[]
): Map<string, string> {
  return items.reduce<Map<string, string>>((acc, item) => {
    const historyId = item.activeVersion.historyId;
    if (historyId) {
      acc.set(item.id, historyId);
    }
    return acc;
  }, new Map());
}

/**
 * Résout les toasts "en cours" des créations d'items lancées depuis cet onglet, en
 * confirmant leur présence dans l'état relu.
 *
 * Le signal SSE annonce qu'il y a eu création sans dire laquelle ; l'identité vient
 * donc du registre local, où l'appelant a lié l'item dès que la mutation a abouti.
 * Un toast par item créé, donc le décompte affiché est exact.
 */
export function resolveCreatedItemToasts(
  items: Map<string, DealItemWithVersions>,
  t: TFunction
): void {
  for (const createdItemId of pendingCreatedItemToastIds()) {
    if (!items.has(createdItemId)) continue;
    replaceDealItemsInProgressWithSuccess(
      {
        title: t('general.success'),
        description: t(
          'components.deal_items_subscription.toast_items_created_one',
          { count: 1 }
        ),
      },
      { createdItemId, kind: 'itemsCreated', fallbackToToast: false }
    );
  }
}

/**
 * Invalide les entrées undo devenues incohérentes avec les `historyId` serveur.
 *
 * Les items en cours d'édition optimiste sont exclus : `mergeStreamedItem`
 * conserve volontairement leur version locale, donc leur `historyId` est périmé et
 * les inclure ferait vider une pile pourtant valide. Une absence de la carte est
 * traitée comme neutre par `isUndoEntryConsistentWithHistoryMap`, ce qui rend
 * l'exclusion inoffensive.
 */
export function invalidateUndoForConfirmedItems(
  items: Map<string, DealItemWithVersions>,
  pendingOptimisticIds: Set<string>,
  invalidateUndoForItems: (historyIds: Map<string, string>) => void
): void {
  const confirmedItems = Array.from(items.values()).filter(
    (item) => !pendingOptimisticIds.has(item.id)
  );
  const historyMap = historyIdMapFromDealItems(confirmedItems);
  if (historyMap.size > 0) invalidateUndoForItems(historyMap);
}

/**
 * Résout un toast de création encore non lié à son item.
 *
 * L'appelant enregistre le toast "en cours" avant d'attendre la mutation et ne lie
 * l'item créé qu'à son retour ; un signal arrivant dans cette fenêtre ne trouve donc
 * aucun tracker lié. Le repli par `kind` de `resolveTrackedToastId` cible exactement
 * un tracker non lié, ce qui reproduit le filet de sécurité du chemin WebSocket.
 * `fallbackToToast: false` garantit qu'aucun toast n'apparaît s'il n'y a rien à
 * résoudre.
 */
export function resolveUnboundCreationToast(t: TFunction): void {
  replaceDealItemsInProgressWithSuccess(
    {
      title: t('general.success'),
      description: t(
        'components.deal_items_subscription.toast_items_created_one',
        { count: 1 }
      ),
    },
    { kind: 'itemsCreated', fallbackToToast: false }
  );
}

type ItemsSignalDeps = {
  action?: DealItemsActionType;
  /** Items à relire, nommés par le signal. */
  itemIds?: string[];
  /** Items partis, nommés par le signal : à retirer sans lecture. */
  deletedItemIds?: string[];
  invalidateDescriptionsCache: () => void;
  itemsBefore: Map<string, DealItemWithVersions>;
  refreshItems: () => Promise<boolean>;
  /** Relit uniquement ces items, sans purger le reste. */
  refreshItemsByIds: (itemIds: string[]) => Promise<boolean>;
  /** Retire ces items de la table, sans lecture. */
  removeItems: (itemIds: string[]) => void;
  readItems: () => Map<string, DealItemWithVersions>;
  readPendingOptimisticIds: () => Set<string>;
  markDatasourceProcessed: () => void;
  markRecentlyModified: (modifications: ProductModification[]) => void;
  invalidateUndoForItems: (historyIds: Map<string, string>) => void;
  t: TFunction;
};

/**
 * Rejoue, à la réception d'un signal d'items du canal SSE, les effets de bord que
 * portait le handler de la subscription WebSocket `dealItemsUpdatedV3`.
 *
 * Aucun de ces effets n'est conditionné à la réussite de la relecture des items :
 * la résolution d'un toast ou le réveil du fallback catégorie ne dépendent pas d'un
 * stream d'items, et les y subordonner laisserait un toast infini bloqué dès qu'une
 * relecture échoue ou est abandonnée.
 *
 * Un signal sans action est un `SYNC`, donc le moment de rattraper ce qui a pu être
 * manqué pendant une coupure. N'y sont rejoués que les effets dont le rattrapage a
 * un sens : purge du cache des descriptions, résolution des créations abouties,
 * invalidation de la pile undo. En sont exclus ceux qui exigent une preuve que
 * l'action a bien eu lieu — le réveil du fallback catégorie, déjà armé par le signal
 * `DEAL` de la transition du datasource, et la résolution d'un toast de création non
 * lié, qui signalerait un succès pour une mutation encore en vol.
 */
export async function applyDealItemsSignal({
  action,
  itemIds,
  deletedItemIds,
  invalidateDescriptionsCache,
  itemsBefore,
  refreshItems,
  refreshItemsByIds,
  removeItems,
  readItems,
  readPendingOptimisticIds,
  markDatasourceProcessed,
  markRecentlyModified,
  invalidateUndoForItems,
  t,
}: ItemsSignalDeps): Promise<void> {
  /**
   * Purge du cache des descriptions pour les actions qui en créent de nouvelles,
   * et sur un `SYNC` — car l'action qui aurait dû le purger a pu être manquée
   * pendant la coupure que ce `SYNC` vient réparer.
   *
   * Le rattrapage est nécessaire : `dealItemDescriptionsNeedFetch` ne teste que
   * `cached === null`, donc une clé manquante ne provoque **aucun** rechargement.
   * Un item créé pendant la coupure resterait absent de la map, et le flux d'items
   * ne transportant pas `description`, sa modale s'ouvrirait vide alors qu'il en a
   * une — que l'utilisateur écraserait en enregistrant.
   *
   * Le coût est nul : ce cache est chargé paresseusement, à l'ouverture d'une
   * description.
   */
  if (
    action === 'datasourceProcessed' ||
    action === 'itemsCreated' ||
    !action
  ) {
    invalidateDescriptionsCache();
  }

  /**
   * Relecture ciblée dès que le serveur nomme les items touchés : sur une grosse offre, une
   * cellule éditée déclenchait le rechargement des mégaoctets de la version entière.
   *
   * Les items supprimés sont retirés sur place, sans lecture : une lecture ciblée ne les
   * renverrait de toute façon jamais. Quand le serveur en nomme, `itemIds` est la liste exacte
   * de ce qui reste à relire, éventuellement vide — une suppression seule ne lit rien.
   *
   * Les deux listes vides veulent dire qu'il ne nomme rien — changement en masse au-delà de ce
   * que la lecture ciblée filtre. On recharge alors tout, comme avant l'existence de ces
   * champs. Même chose sur un `SYNC`, qui n'a pas d'items.
   */
  console.debug('[applyDealItemsSignal] signal reçu', {
    action,
    itemIds,
    deletedItemIds,
  });
  const deleted = deletedItemIds ?? [];
  if (deleted.length > 0) removeItems(deleted);
  if (itemIds?.length) {
    await refreshItemsByIds(itemIds);
  } else if (deleted.length === 0) {
    await refreshItems();
  }

  const itemsAfter = readItems();

  if (action === 'datasourceProcessed') markDatasourceProcessed();

  if (action) {
    const modifications = diffHighlightableFields({
      previous: itemsBefore,
      current: itemsAfter,
    });
    if (modifications.length > 0) markRecentlyModified(modifications);
  }

  resolveCreatedItemToasts(itemsAfter, t);

  /**
   * Volontairement absent du `SYNC` : un tracker non lié signifie que la mutation
   * de création n'a pas encore répondu, donc le résoudre afficherait un succès
   * pour une création encore en vol — et si elle échoue ensuite, l'appelant ne
   * trouve plus rien à nettoyer et le faux succès reste à l'écran.
   *
   * Le rattrapage d'une création réellement aboutie est déjà assuré par
   * {@link resolveCreatedItemToasts}, appelé sans condition juste au-dessus.
   */
  if (action === 'itemsCreated') {
    resolveUnboundCreationToast(t);
  }

  if (action === 'itemsUpdated' || !action) {
    invalidateUndoForConfirmedItems(
      itemsAfter,
      readPendingOptimisticIds(),
      invalidateUndoForItems
    );
  }
}

export function useDealItems(dealId: string) {
  const store = useStore();
  const setProductsMap = useSetAtom(productsMapState);
  const setDealItemsRefetch = useSetAtom(dealItemsRefetchState);
  const setDealItemsSignal = useSetAtom(dealItemsSignalState);
  const setPendingOptimisticUpdates = useSetAtom(pendingOptimisticUpdatesState);
  const setRecentlyModified = useSetAtom(recentlyModifiedProductsState);
  const { invalidateUndoForItems } = useUndoDealItems(dealId);
  const { t } = useTranslation();
  const { getAccessTokenSilently } = useAuth0();

  const notifyRefreshFailed = useCallback(() => {
    toast({
      title: t('general.error'),
      description: t('components.deal_items_subscription.toast_refresh_failed'),
      variant: 'destructive',
    });
  }, [t]);

  const notifyRefreshFailedRef = useRef(notifyRefreshFailed);
  notifyRefreshFailedRef.current = notifyRefreshFailed;

  const refetchDealItems = useCallback(async (): Promise<boolean> => {
    const result = await ensureDealItemsStream({
      dealId,
      isRefresh: true,
      getToken: getAccessTokenSilently,
    });
    if (result === 'error') {
      notifyRefreshFailed();
    }
    return result === 'success';
  }, [dealId, getAccessTokenSilently, notifyRefreshFailed]);

  useEffect(() => {
    const registration = { fn: refetchDealItems };
    setDealItemsRefetch(registration);
    return () => {
      setDealItemsRefetch((current) =>
        current === registration ? null : current
      );
    };
  }, [setDealItemsRefetch, refetchDealItems]);

  const refreshItemsByIds = useCallback(
    async (itemIds: string[]): Promise<boolean> => {
      const result = await refreshDealItemsByIds({
        dealId,
        itemIds,
        getToken: getAccessTokenSilently,
      });
      // Un échec ciblé n'est pas rattrapé par un autre signal : on retombe sur la relecture
      // complète plutôt que de laisser les lignes périmées à l'écran.
      if (result === 'error') return refetchDealItems();
      return result === 'success';
    },
    [dealId, getAccessTokenSilently, refetchDealItems]
  );

  const removeItems = useCallback(
    (itemIds: string[]): void => removeDealItems(dealId, itemIds),
    [dealId]
  );

  const handleItemsSignal = useCallback(
    (
      action?: DealItemsActionType,
      itemIds?: string[],
      deletedItemIds?: string[]
    ): Promise<void> =>
      applyDealItemsSignal({
        action,
        itemIds,
        deletedItemIds,
        invalidateDescriptionsCache: () =>
          store.set(dealItemDescriptionsByActiveVersionIdState, null),
        itemsBefore: store.get(productsMapState),
        refreshItems: refetchDealItems,
        refreshItemsByIds,
        removeItems,
        readItems: () => store.get(productsMapState),
        readPendingOptimisticIds: () =>
          store.get(pendingOptimisticUpdatesState),
        markDatasourceProcessed: () =>
          store.set(datasourceProcessedSignalAtom, (n) => n + 1),
        markRecentlyModified: (modifications) =>
          setRecentlyModified((prev) =>
            updateRecentlyModifiedProducts(prev, Date.now(), modifications)
          ),
        invalidateUndoForItems,
        t,
      }),
    [
      invalidateUndoForItems,
      refetchDealItems,
      refreshItemsByIds,
      removeItems,
      setRecentlyModified,
      store,
      t,
    ]
  );

  useEffect(() => {
    const registration = { fn: handleItemsSignal };
    setDealItemsSignal(registration);
    return () => {
      setDealItemsSignal((current) =>
        current === registration ? null : current
      );
    };
  }, [setDealItemsSignal, handleItemsSignal]);

  useEffect(() => {
    if (!dealId) return;
    let cancelled = false;
    void ensureDealItemsStream({
      dealId,
      isRefresh: false,
      getToken: getAccessTokenSilently,
    }).then((result) => {
      if (!cancelled && result === 'error') {
        notifyRefreshFailedRef.current();
      }
    });
    return () => {
      cancelled = true;
      stopDealItemsStream(dealId);
      resetValidationWorker();
    };
  }, [dealId, getAccessTokenSilently]);

  // Subscribe to deal items updates to keep cache in sync.
  // Skipped when the SSE signal channel is enabled — in that mode a
  // coarse refetch (driven from `page-layout` via `useDealEventStream`) takes
  // over. The rich per-item merge/toast handling below is WS-only for now.
  useSubscription(DealItemsUpdatedV3Document, {
    variables: { dealId },
    skip: !dealId || isDealEventsSseEnabled(),
    onData: async ({ data: subscriptionData, client: apolloClient }) => {
      const payload = subscriptionData.data?.dealItemsUpdatedV3;
      const fromAction = payload?.fromAction;
      const updatedItems = payload?.items ?? [];

      /**
       * Suppression : le serveur renvoie une liste d'items vide, les identifiants
       * supprimés n'étant plus en base. On se contente donc de resynchroniser le
       * cache.
       *
       * Aucun toast ici : l'onglet qui supprime en affiche déjà un depuis son
       * appel de mutation, et cette branche s'exécute sur **tous** les onglets
       * abonnés — elle en produisait donc un second sur l'onglet à l'origine de
       * la suppression, et un toast non sollicité sur les autres. Le canal SSE
       * n'en émet pas non plus.
       */
      if (fromAction?.type === 'itemsDeleted') {
        store.set(dealItemDescriptionsByActiveVersionIdState, null);
        await refetchDealItems();
        return;
      }

      // datasourceProcessed with empty items: resolve real result via GetDatasource and open the import report.
      if (fromAction?.type === 'datasourceProcessed' && !updatedItems.length) {
        store.set(datasourceProcessedSignalAtom, (n) => n + 1);
        void showImportReportFromProcessedDatasource(
          apolloClient,
          dealId,
          fromAction.datasourceId ?? undefined,
          t
        );
        return;
      }

      // For other actions, we need items to merge into state
      if (!updatedItems.length) return;

      const fromType = fromAction?.type;
      const shouldInvalidateDescriptionCache =
        fromType === 'datasourceProcessed' || fromType === 'itemsCreated';
      if (shouldInvalidateDescriptionCache) {
        store.set(dealItemDescriptionsByActiveVersionIdState, null);
      }

      // Jotai est la source de vérité pour l'UI — on lit la Map directement,
      // O(k) Map lookups au lieu de readQuery O(n) + writeQuery O(n) Apollo.
      const currentMap = store.get(productsMapState);
      const currentPendingUpdates = store.get(pendingOptimisticUpdatesState);
      const newItems = updatedItems
        .filter((update) => !currentMap.has(update.dealItemWithVersions.id))
        .map((update) => update.dealItemWithVersions);

      const updatedItemIds = new Set(
        updatedItems.map((u) => u.dealItemWithVersions.id)
      );
      const processedPendingIds = new Set<string>();
      currentPendingUpdates.forEach((id) => {
        if (updatedItemIds.has(id) || !currentMap.has(id))
          processedPendingIds.add(id);
      });

      setProductsMap((prev) => {
        const next = new Map(prev);

        for (const update of updatedItems) {
          const normalized = normalizeSingle(update.dealItemWithVersions);
          next.set(
            normalized.id,
            mergeStreamedItem(
              normalized,
              prev.get(normalized.id),
              currentPendingUpdates
            )
          );
        }

        return next;
      });

      if (processedPendingIds.size > 0) {
        setPendingOptimisticUpdates((prev) => {
          const next = new Set(prev);
          processedPendingIds.forEach((id) => next.delete(id));
          return next;
        });
      }

      if (fromType === 'datasourceProcessed') {
        store.set(datasourceProcessedSignalAtom, (n) => n + 1);
      }

      markBackendConfirmed(Array.from(updatedItemIds));

      // Mark products and fields as recently modified for highlighting
      // This covers: itemsUpdated, itemsCreated, datasourceProcessed
      // Backend may not send changedFields for itemsCreated/datasourceProcessed
      // (CSV, scraping, copy previous sales) — use all highlightable fields as fallback
      const isBulkImportOrCreate =
        fromAction?.type === 'itemsCreated' ||
        fromAction?.type === 'datasourceProcessed';
      const now = Date.now();
      const modifications: ProductModification[] = updatedItems.map(
        (update) => {
          const backendFields = update.changedFields ?? [];
          const fields =
            backendFields.length > 0
              ? backendFields
              : isBulkImportOrCreate
                ? ALL_HIGHLIGHTABLE_FIELDS
                : [];
          return {
            productId: update.dealItemWithVersions.id,
            fields,
          };
        }
      );

      setRecentlyModified((prev) =>
        updateRecentlyModifiedProducts(prev, now, modifications)
      );

      // Invalidate undo stack entries when items are updated by another source.
      // On ne passe que les items modifiés (pas tout le tableau) — suffisant pour
      // valider les entrées undo concernées.
      if (fromAction?.type === 'itemsUpdated') {
        const historyMap = historyIdMapFromDealItems(
          updatedItems.map((u) => u.dealItemWithVersions)
        );
        if (historyMap.size > 0) {
          invalidateUndoForItems(historyMap);
        }
      }

      /**
       * Toasts de succès : la subscription est la seule source pour
       * `itemsCreated`. Les résultats de datasource passent par le rapport
       * d'import. Les toasts "en cours" sont posés par chaque appelant au
       * lancement de sa mutation, et on ne duplique jamais le succès ici.
       * (`itemsUpdated` : pas de toast, pour éviter le bruit.)
       */
      if (fromAction?.type === 'itemsCreated' && newItems.length > 0) {
        const key =
          newItems.length === 1
            ? 'components.deal_items_subscription.toast_items_created_one'
            : 'components.deal_items_subscription.toast_items_created_other';
        replaceDealItemsInProgressWithSuccess(
          {
            title: t('general.success'),
            description: t(key, { count: newItems.length }),
          },
          {
            createdItemId: newItems.length === 1 ? newItems[0]?.id : undefined,
            kind: 'itemsCreated',
            fallbackToToast: false,
          }
        );
      }
      if (fromAction?.type === 'datasourceProcessed') {
        void showImportReportFromProcessedDatasource(
          apolloClient,
          dealId,
          fromAction.datasourceId ?? undefined,
          t
        );
      }
    },
  });

  return {
    refetch: refetchDealItems,
  };
}
